import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

/**
 * Hooks around /api/v1/gallery-images/ — the admin-only CRUD surface
 * backing the public photo gallery. The viewset is locked to Admin
 * role, so non-admin users will see a 403 from any of these.
 */

export interface GalleryImageRow {
  id: number;
  title: string;
  caption: string;
  image_url: string | null;
  is_featured: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const KEY = ["gallery-images"];

// ---------- READ ----------

/** GET /api/v1/gallery-images/ — admin only. */
export function useGalleryImages(opts: { active?: boolean | null } = {}) {
  return useQuery<GalleryImageRow[]>({
    queryKey: [...KEY, "list", opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.active === true)  params.set("active", "true");
      if (opts.active === false) params.set("active", "false");
      const { data } = await api.get<GalleryImageRow[] | { results: GalleryImageRow[] }>(
        `/gallery-images/${params.toString() ? "?" + params.toString() : ""}`,
      );
      // Defensive: viewset has pagination_class=None so the bare list is
      // the normal shape, but normalise in case that changes upstream.
      return Array.isArray(data) ? data : (data?.results ?? []);
    },
    staleTime: 60_000,
  });
}

// ---------- WRITE ----------

export interface CreateGalleryImageInput {
  image: File;
  title?: string;
  caption?: string;
  is_featured?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

export function useCreateGalleryImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGalleryImageInput) => {
      const form = new FormData();
      form.append("image", input.image);
      if (input.title       != null) form.append("title",       input.title);
      if (input.caption     != null) form.append("caption",     input.caption);
      if (input.is_featured != null) form.append("is_featured", String(input.is_featured));
      if (input.is_active   != null) form.append("is_active",   String(input.is_active));
      if (input.sort_order  != null) form.append("sort_order",  String(input.sort_order));
      // Axios picks Content-Type=multipart/form-data automatically.
      const { data } = await api.post<GalleryImageRow>("/gallery-images/", form);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface UpdateGalleryImageInput {
  id: number;
  title?: string;
  caption?: string;
  image?: File;  // optional — only when swapping the photo
  is_featured?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

export function useUpdateGalleryImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: UpdateGalleryImageInput) => {
      const form = new FormData();
      if (rest.title       != null) form.append("title",       rest.title);
      if (rest.caption     != null) form.append("caption",     rest.caption);
      if (rest.image       != null) form.append("image",       rest.image);
      if (rest.is_featured != null) form.append("is_featured", String(rest.is_featured));
      if (rest.is_active   != null) form.append("is_active",   String(rest.is_active));
      if (rest.sort_order  != null) form.append("sort_order",  String(rest.sort_order));
      const { data } = await api.patch<GalleryImageRow>(`/gallery-images/${id}/`, form);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteGalleryImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/gallery-images/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ---------- BULK WRITE ----------

/** PATCH the same fields onto every id. Fires N requests in parallel,
 *  invalidates the cache once at the end. Resolves to a count of
 *  succeeded vs failed so the UI can summarise. */
export function useBulkUpdateGalleryImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: number[];
      patch: Partial<{ is_active: boolean; is_featured: boolean }>;
    }) => {
      const results = await Promise.allSettled(
        input.ids.map((id) => {
          const form = new FormData();
          if (input.patch.is_active   != null) form.append("is_active",   String(input.patch.is_active));
          if (input.patch.is_featured != null) form.append("is_featured", String(input.patch.is_featured));
          return api.patch(`/gallery-images/${id}/`, form);
        }),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      return { ok, fail };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** DELETE every id in parallel, invalidate once at the end. */
export function useBulkDeleteGalleryImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`/gallery-images/${id}/`)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      return { ok, fail };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
